import { LocalStorageKey } from "@/utils/local-storage-utils";
import { useCallback, useRef, useSyncExternalStore, useMemo } from "react";
import fastSafeStringify from "fast-safe-stringify";

// Map to store local listeners for each key (storage event doesn't fire in same tab)
const localListeners = new Map<LocalStorageKey, Set<() => void>>();

/**
 * Notify all local listeners for a given key that the value has changed
 */
function notifyLocalListeners(key: LocalStorageKey): void {
  const listeners = localListeners.get(key);
  if (listeners) {
    listeners.forEach((listener) => listener());
  }
}

/**
 * Get a value from local storage and parse it as JSON
 */
function getLocalStorageItem(key: LocalStorageKey): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  return localStorage.getItem(key);
}

function parseLocalStorageItem<T extends Record<string, unknown>>(
  key: LocalStorageKey,
  localStringValue: string | null,
  fallbackValue: T,
): T {
  if (!localStringValue) {
    return fallbackValue;
  }

  try {
    return JSON.parse(localStringValue) as T;
  } catch (err) {
    console.error(`[Chalk] Local storage key ${key} failed to parse with error: ${err}`);
    return fallbackValue;
  }
}

/**
 * Set a value in local storage by stringifying it as JSON
 */
function setLocalStorageItem<T extends Record<string, unknown>>(
  key: LocalStorageKey,
  value: T,
): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(key, fastSafeStringify.stableStringify(value));
    // Notify local listeners since storage event won't fire in same tab
    notifyLocalListeners(key);
  } catch (err) {
    console.error(`[Chalk] Local storage key ${key} failed to save with error: ${err}`);
  }
}

/**
 * Remove a value from local storage
 */
function removeLocalStorageItem(key: LocalStorageKey): void {
  if (typeof window === "undefined") {
    return;
  }

  localStorage.removeItem(key);
  // Notify local listeners since storage event won't fire in same tab
  notifyLocalListeners(key);
}

/**
 * React hook to persist and sync JSON data to local storage across tabs
 *
 * @param key - The LocalStorageKey to store the value under
 * @param defaultValue - The default value to use if no value is stored
 * @returns A tuple matching the useState hook, the first value being the stored value
 *          and the second being the updater function.
 *
 * @example
 * ```tsx
 * const [options, setOptions] = useJsonLocalStorage(
 *   LocalStorageKey.ADMIN_OPTIONS,
 *   { useJsonGrpcTransport: false }
 * );
 * ```
 */
export function useLocalStorageSyncedState<T extends Record<string, unknown>>(
  key: LocalStorageKey,
  defaultValue: T,
): [T, (valueOrUpdater: ((oldVal: T) => T | null) | T | null) => void] {
  const valueRef = useRef<T>(defaultValue);
  // Subscribe function for useSyncExternalStore
  const subscribe = useCallback(
    (callback: () => void) => {
      // Get or create the set of listeners for this key
      if (!localListeners.has(key)) {
        localListeners.set(key, new Set());
      }

      const listeners = localListeners.get(key)!;

      // Add this callback to the set
      listeners.add(callback);
      // Subscribe to storage events from other tabs
      const windowEventListener = (event: StorageEvent) => {
        if (event.key === key) {
          callback();
        }
      };
      if (typeof window !== "undefined") {
        window.addEventListener("storage", windowEventListener);
      }

      // Return cleanup function
      return () => {
        listeners.delete(callback);
        if (typeof window !== "undefined") {
          window.removeEventListener("storage", windowEventListener);
        }
      };
    },
    [key],
  );

  // Get snapshot function for useSyncExternalStore
  const getSnapshot = useCallback(() => {
    return getLocalStorageItem(key);
  }, [key]);

  // for SSR
  const getServerSnapshot = useCallback(() => {
    return JSON.stringify(defaultValue);
  }, [defaultValue]);

  const localStringValue = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Note: must do this in hook since this must be cached across the same string
  const value = useMemo(() => {
    valueRef.current = parseLocalStorageItem(key, localStringValue, defaultValue);

    return valueRef.current;
  }, [localStringValue, defaultValue, key]);

  // Setter function
  const setValue = useCallback(
    (valueOrUpdater: ((oldVal: T) => T | null) | T | null) => {
      const currentValue =
        typeof valueOrUpdater === "function"
          ? parseLocalStorageItem(key, getLocalStorageItem(key), valueRef.current)
          : valueRef.current;
      const newValue =
        typeof valueOrUpdater === "function" ? valueOrUpdater(currentValue) : valueOrUpdater;
      // Functional updaters can return the current snapshot to suppress a no-op write.
      if (typeof valueOrUpdater === "function" && Object.is(newValue, currentValue)) {
        return;
      }
      if (newValue === null) {
        valueRef.current = defaultValue;
        removeLocalStorageItem(key);
      } else {
        valueRef.current = newValue;
        setLocalStorageItem(key, newValue);
      }
    },
    [defaultValue, key],
  );

  return [value, setValue];
}
